import { z } from 'zod'
import { DEVICE_CATEGORIES, DIRECTION_LABELS, FEEDBACK_KINDS, PORT_DIRECTIONS, POWER_DRAW_SOURCES, POWER_SOURCES, SIGNAL_CATEGORIES, SLOT_BUSES } from './signals'
import { SHOW_FORMAT_KEYS } from './formats'
import { CABLE_GRADES, HDMI_GRADES } from './cable-grades'


/** Watts: positive or absent. Never 0 — unknown must not read as free. */
const optionalWatts = z.number().positive().max(100_000).nullish()

const trimmed = (max: number) => z.string().trim().min(1).max(max)

/**
 * True for an empty string, a real IPv4 address, or a hostname.
 * Octet *ranges* matter: a digit-count-only regex accepts 999.999.1.1.
 */
export function isValidHost(value: string): boolean {
  if (value === '') return true

  const octets = value.split('.')
  if (octets.length === 4 && octets.every((octet) => /^\d{1,3}$/.test(octet))) {
    return octets.every((octet) => Number(octet) <= 255)
  }

  return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(
    value,
  )
}

/** Empty string, an IPv4 address, or a hostname. Stored as null when blank. */
const optionalIp = z
  .string()
  .trim()
  .max(60)
  .refine(isValidHost, { message: 'Enter a valid IPv4 address or hostname' })
  .transform((v) => (v === '' ? null : v))
  .nullish()

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullish()

export const portInputSchema = z.object({
  /** Present when editing an existing port. Ports keep their identity across
   *  device edits so cable runs already drawn on a canvas survive. */
  id: z.string().trim().max(40).optional(),
  name: trimmed(80),
  direction: z.enum(PORT_DIRECTIONS),
  connector: trimmed(60),
  signal: z.enum(SIGNAL_CATEGORIES),
  position: z.number().int().min(0).max(999).default(0),
})

/** One expansion slot on a computer. */
export const slotInputSchema = z.object({
  /** Present when editing; slots keep their identity so the ports copied into
   *  them (and the cables on those ports) survive a save. */
  id: z.string().trim().max(40).optional(),
  label: trimmed(40),
  bus: z.enum(SLOT_BUSES).default('PCIE'),
  /** Installed card (a CAPTURE_CARD device). Blank = empty slot. */
  cardId: z
    .string()
    .trim()
    .max(40)
    .nullish()
    .transform((v) => v || null),
})

/**
 * An optional http(s) URL. Used by every field that is rendered as a link or
 * that backs a published figure: a `sourceUrl` accepting any string was both
 * a link with no scheme check and a citation that need not cite anything.
 */
function httpUrl(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v === null || /^https?:\/\/\S+$/i.test(v), { message: 'Enter an http(s) URL' })
    .nullish()
}

export const deviceInputSchema = z
  .object({
    name: trimmed(120),
    manufacturer: trimmed(120),
    model: trimmed(120),
    /** Omitted on create → inferred from the name; omitted on update → kept. */
    category: z.enum(DEVICE_CATEGORIES).optional(),
    cpu: optionalText(120),
    ram: optionalText(120),
    gpu: optionalText(120),
    storage: optionalText(120),
    os: optionalText(120),
    formFactorRU: z.number().int().min(0).max(60).default(1),
    ipAddress: optionalIp,
    notes: optionalText(2000),
    sourceUrl: httpUrl(500),
    verified: z.boolean().default(false),
    /** Power figures. Kept as three separate numbers on purpose — a PSU
     *  nameplate is not a draw figure and must never stand in for one. */
    powerDrawTypicalW: optionalWatts,
    powerDrawMaxW: optionalWatts,
    powerSupplyRatedW: optionalWatts,
    powerSource: z.enum(POWER_SOURCES).default('UNKNOWN'),
    powerDrawSource: z.enum(POWER_DRAW_SOURCES).default('UNKNOWN'),
    powerNote: optionalText(500),
    poePdDrawW: optionalWatts,
    poeType: z.number().int().min(1).max(4).nullish(),
    poeBudgetW: optionalWatts,
    mainsRatingA: z.number().positive().max(1000).nullish(),
    /** Onboard ports only. Ports copied from an installed card are managed
     *  through `slots`; echoing one of their ids here is rejected. */
    ports: z.array(portInputSchema).max(256).default([]),
    /** Omitted → existing slots untouched; [] → all removed. */
    slots: z.array(slotInputSchema).max(16).optional(),
    /** Set by the editor once the operator has seen the real number of cable
     *  runs a port removal would delete and said yes. Never honoured for a
     *  catalogue device: those runs belong to workspaces whoever is editing
     *  cannot see, so that case refuses outright. */
    confirmRunLoss: z.boolean().default(false),
  })
  .superRefine((device, ctx) => {
    // These four mirror the CHECK constraints in the power_model migration, so
    // a bad payload is a 422 with a field path rather than a 500 from Postgres.
    if (device.powerDrawTypicalW != null && device.powerDrawMaxW == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['powerDrawMaxW'],
        message: 'A single published figure is a maximum. Enter it as the maximum, not the typical draw.',
      })
    }
    if (
      device.powerDrawTypicalW != null &&
      device.powerDrawMaxW != null &&
      device.powerDrawTypicalW > device.powerDrawMaxW
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['powerDrawTypicalW'],
        message: 'Typical draw cannot exceed maximum draw.',
      })
    }
    if (
      device.powerDrawSource === 'PUBLISHED' &&
      device.powerDrawMaxW == null &&
      device.poePdDrawW == null
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['powerDrawSource'],
        message: 'Published means a figure was printed on the spec page — enter it, or set the source to Unknown.',
      })
    }
    if (device.powerDrawSource === 'DERIVED_FROM_PSU' && device.powerSupplyRatedW == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['powerSupplyRatedW'],
        message: 'Derived from a PSU rating needs the rating it was derived from.',
      })
    }

    // The DB unique on Port no longer covers onboard ports (NULL slotId is
    // distinct in Postgres), so the check lives here — the same arrangement
    // as catalogue uniqueness in devices.ts.
    const seenPorts = new Set<string>()
    device.ports.forEach((port, index) => {
      const key = `${port.direction}:${port.name.toLowerCase()}`
      if (seenPorts.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['ports', index, 'name'],
          message: `Two ${DIRECTION_LABELS[port.direction].toLowerCase()} ports are named "${port.name}"`,
        })
      }
      seenPorts.add(key)
    })
    const seenLabels = new Set<string>()
    device.slots?.forEach((slot, index) => {
      const key = slot.label.toLowerCase()
      if (seenLabels.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['slots', index, 'label'],
          message: `Two slots are labelled "${slot.label}"`,
        })
      }
      seenLabels.add(key)
    })
  })

/**
 * A wattage a workspace supplies for a device the catalogue has no figure for.
 *
 * Narrower than the catalogue's own columns on purpose. There is no
 * DERIVED_FROM_PSU here — a nameplate is not a draw, and somebody typing a
 * number into a form is not reading one off an adapter by accident — and no
 * UNKNOWN, because a row with no provenance is the absence this exists to fill.
 */
export const powerFigureSchema = z
  .object({
    powerDrawMaxW: z.number().positive().max(100_000),
    powerDrawTypicalW: optionalWatts,
    provenance: z.enum(['PUBLISHED', 'MEASURED']),
    sourceUrl: httpUrl(500),
    note: optionalText(500),
  })
  .superRefine((figure, ctx) => {
    // Rule 1: published means there is a page to point at. The database has the
    // same CHECK; this one carries a field path so the form can mark the input.
    if (figure.provenance === 'PUBLISHED' && !figure.sourceUrl) {
      ctx.addIssue({
        code: 'custom',
        path: ['sourceUrl'],
        message: 'A published figure needs the page it came from. If you measured it, choose Measured instead.',
      })
    }
    if (figure.powerDrawTypicalW != null && figure.powerDrawTypicalW > figure.powerDrawMaxW) {
      ctx.addIssue({
        code: 'custom',
        path: ['powerDrawTypicalW'],
        message: 'Typical draw cannot be higher than the maximum.',
      })
    }
  })

export type PowerFigureInput = z.infer<typeof powerFigureSchema>

export type DeviceInput = z.infer<typeof deviceInputSchema>
export type PortInput = z.infer<typeof portInputSchema>
export type SlotInput = z.infer<typeof slotInputSchema>

/** A brand row: the exact manufacturer string plus an optional website. */
export const brandInputSchema = z.object({
  name: trimmed(120),
  website: httpUrl(300),
})

export type BrandInput = z.infer<typeof brandInputSchema>

export const projectInputSchema = z.object({
  name: trimmed(120),
  description: optionalText(1000),
})

const canvasNodeSchema = z.object({
  id: trimmed(40),
  deviceId: trimmed(40),
  label: optionalText(120),
  x: z.number().finite(),
  y: z.number().finite(),
  inventoryItemId: optionalText(40),
})

const canvasEdgeSchema = z.object({
  id: trimmed(40),
  sourceNodeId: trimmed(40),
  sourcePortId: trimmed(40),
  targetNodeId: trimmed(40),
  targetPortId: trimmed(40),
  label: optionalText(60),
  /**
   * Run length in metres. Rides in the canvas payload rather than having its
   * own endpoint, because saving replaces every edge — a length kept anywhere
   * else would be wiped by the next save. Bounds mirror the DB CHECK.
   */
  lengthM: z.number().positive().max(10000).nullish(),
  /** Coax grade or HDMI certification; the DB CHECK holds the vocabulary. */
  cableGrade: z.enum([...CABLE_GRADES, ...HDMI_GRADES]).nullish(),
  /** This run only — the exception, not the rule. */
  formatOverride: z.enum(SHOW_FORMAT_KEYS as [string, ...string[]]).nullish(),
  /** The catalogue cable this run uses, when it names one. */
  cableId: optionalText(40),
})

/**
 * A full canvas snapshot. Saving replaces the project's nodes and edges
 * wholesale inside one transaction — simple, and correct for a single-editor
 * tool (multiplayer is explicitly out of scope for V1).
 */
export const canvasSaveSchema = z.object({
  nodes: z.array(canvasNodeSchema).max(500),
  edges: z.array(canvasEdgeSchema).max(2000),
  /** The format the whole show runs at. Null until somebody sets it. */
  showFormat: z.enum(SHOW_FORMAT_KEYS as [string, ...string[]]).nullish(),
})

export type CanvasSave = z.infer<typeof canvasSaveSchema>

export const workspaceInputSchema = z.object({
  name: z.string().trim().min(2).max(60),
})

import { INVENTORY_STATUSES } from './signals'

export const inventoryItemPatchSchema = z.object({
  assetTag: optionalText(60),
  serialNumber: optionalText(80),
  status: z.enum(INVENTORY_STATUSES).optional(),
  location: optionalText(120),
  notes: optionalText(1000),
})

/** Adds N identical units of one device in a single call. */
export const inventoryBulkAddSchema = z.object({
  deviceId: trimmed(40),
  count: z.number().int().min(1).max(100).default(1),
  /** "CAM" → CAM-01, CAM-02, … Blank leaves asset tags empty. */
  assetTagPrefix: optionalText(20),
  serialNumbers: z.array(z.string().trim().max(80)).max(100).optional(),
  status: z.enum(INVENTORY_STATUSES).default('AVAILABLE'),
  location: optionalText(120),
})

export const inviteInputSchema = z.object({
  role: z.enum(['ADMIN', 'MEMBER']).default('MEMBER'),
  expiresInDays: z.number().int().min(1).max(90).default(14),
  maxUses: z.number().int().min(1).max(500).nullish(),
})

export const memberRoleSchema = z.object({
  role: z.enum(['OWNER', 'ADMIN', 'MEMBER']),
})

/* ------------------------------- power plans ------------------------------ */

const powerPlanGroupSchema = z.object({
  id: trimmed(40),
  name: trimmed(60),
  /** The protective device this group sits behind. Null when not yet known. */
  breakerA: z.number().positive().max(1000).nullish(),
  leg: z.enum(['L1', 'L2', 'L3']).nullish(),
  /** Simultaneity, for the demand column only. 1 = everything at once. */
  diversity: z.number().min(0.1).max(1).default(1),
  upstream: z
    .object({
      efficiency: z.number().min(0.5).max(1),
      idleW: z.number().min(0).max(5000),
    })
    .nullish(),
})

const powerPlanItemSchema = z.object({
  id: trimmed(40),
  kind: z.enum(['DEVICE', 'PRESET', 'MANUAL']),
  deviceId: optionalText(40),
  presetKey: optionalText(60),
  label: trimmed(120),
  qty: z.number().int().min(1).max(999),
  /** More than one only when the row is genuinely redundant. */
  groupIds: z.array(trimmed(40)).max(4).default([]),
  wattsTypical: z.number().positive().max(100_000).nullish(),
  wattsMax: z.number().positive().max(100_000).nullish(),
  powerFactor: z.number().min(0.3).max(1).nullish(),
  pfPreset: z.enum(['RESISTIVE', 'MOTOR', 'SWITCHING']).nullish(),
  powerSource: z.enum(POWER_SOURCES).nullish(),
  hostItemId: optionalText(40),
  inrush: z.enum(['NONE', 'PSU', 'LAMP', 'MOTOR', 'TRANSFORMER']).nullish(),
  note: optionalText(500),
  units: z.number().positive().max(10_000).nullish(),
  /** What the catalogue said when this row was added, for drift reporting. */
  catalogueWattsAtSnapshot: z.number().positive().max(100_000).nullish(),
})

/**
 * A whole plan snapshot. Bounded like `canvasSaveSchema` — a power plan is a
 * document someone reads on paper, so a 500-row plan is already past the point
 * of being useful and a 5,000-row one is a bug or an attack.
 */
export const powerPlanSnapshotSchema = z
  .object({
    schemaVersion: z.literal(1),
    settings: z.object({
      voltage: z.number().min(100).max(480),
      frequency: z.union([z.literal(50), z.literal(60)]),
      headroomPct: z.number().min(0).max(100),
      /** 100 = IEC, no continuous-load derate. 80 = the NEC policy. */
      usablePct: z.number().min(50).max(100),
      contentFraction: z.number().min(0.05).max(1),
      showHours: z.number().min(0.5).max(48),
      supply: z.union([
        z.object({ kind: z.literal('NONE') }),
        z.object({
          kind: z.literal('GENSET'),
          kva: z.number().positive().max(5000),
          rating: z.enum(['PRIME', 'STANDBY']).optional(),
        }),
        z.object({ kind: z.literal('PLN'), tierVa: z.number().positive().max(1_000_000) }),
      ]),
      worstCaseVoltage: z.boolean().default(false),
    }),
    groups: z.array(powerPlanGroupSchema).max(40),
    items: z.array(powerPlanItemSchema).max(500),
  })
  .superRefine((plan, ctx) => {
    const groupIds = new Set(plan.groups.map((g) => g.id))
    if (groupIds.size !== plan.groups.length) {
      ctx.addIssue({ code: 'custom', path: ['groups'], message: 'Group ids must be unique.' })
    }
    const itemIds = new Set(plan.items.map((i) => i.id))
    if (itemIds.size !== plan.items.length) {
      ctx.addIssue({ code: 'custom', path: ['items'], message: 'Row ids must be unique.' })
    }
    plan.items.forEach((item, index) => {
      for (const groupId of item.groupIds) {
        if (!groupIds.has(groupId)) {
          ctx.addIssue({
            code: 'custom',
            path: ['items', index, 'groupIds'],
            message: `Row is assigned to group "${groupId}", which is not in this plan.`,
          })
        }
      }
      if (item.hostItemId && !itemIds.has(item.hostItemId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'hostItemId'],
          message: 'The row said to carry this load is not in this plan.',
        })
      }
      // A row cannot be its own host: the attribution pass would never settle.
      if (item.hostItemId === item.id) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'hostItemId'],
          message: 'A row cannot be powered by itself.',
        })
      }
      if (item.kind === 'DEVICE' && !item.deviceId) {
        ctx.addIssue({ code: 'custom', path: ['items', index, 'deviceId'], message: 'A device row needs a device.' })
      }
      if (item.kind === 'PRESET' && !item.presetKey) {
        ctx.addIssue({ code: 'custom', path: ['items', index, 'presetKey'], message: 'A preset row needs a preset.' })
      }
      if (item.wattsTypical != null && item.wattsMax == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'wattsMax'],
          message: 'A single figure is a maximum. Enter it as the maximum.',
        })
      }
      if (item.wattsTypical != null && item.wattsMax != null && item.wattsTypical > item.wattsMax) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'wattsTypical'],
          message: 'Typical draw cannot exceed maximum draw.',
        })
      }
    })
    // Host chains must terminate, or the attribution pass loops.
    const hostOf = new Map(plan.items.map((i) => [i.id, i.hostItemId ?? null]))
    for (const item of plan.items) {
      const seen = new Set<string>([item.id])
      let cursor = hostOf.get(item.id) ?? null
      while (cursor) {
        if (seen.has(cursor)) {
          ctx.addIssue({ code: 'custom', path: ['items'], message: 'Rows cannot power each other in a circle.' })
          break
        }
        seen.add(cursor)
        cursor = hostOf.get(cursor) ?? null
      }
    }
  })

export const powerPlanInputSchema = z.object({
  name: trimmed(120),
  projectId: optionalText(40),
  notes: optionalText(2000),
  snapshot: powerPlanSnapshotSchema,
})

export type PowerPlanInput = z.infer<typeof powerPlanInputSchema>

/**
 * The create body before the snapshot exists. `seedFromProjectId` fills the
 * rows from a canvas, so `snapshot` arrives built rather than sent; everything
 * else is checked by powerPlanInputSchema once it has been.
 *
 * This exists so the handler never reads a property off an unvalidated body:
 * `request.json()` will happily hand back null, a string or an array.
 */
export const powerPlanSeedSchema = z
  .object({
    seedFromProjectId: optionalText(40),
    name: z.string().optional(),
    projectId: z.string().optional(),
    notes: z.string().optional(),
    snapshot: z.unknown().optional(),
  })
  .passthrough()


/**
 * A report from inside the app.
 *
 * Only two fields come from the browser. Everything else that ends up on the
 * row — who sent it, which workspace, which page, which build — is derived on
 * the server, because a value the client can set is a value the client can lie
 * about, and no database constraint can tell the difference.
 */
export const feedbackInputSchema = z
  .object({
    kind: z.enum(FEEDBACK_KINDS),
    message: trimmed(4000),
  })
  .strict()

/** Resolving one. `reply` is shown to whoever sent it, so it is not a scratch note. */
export const feedbackResolveSchema = z
  .object({
    resolved: z.boolean(),
    reply: z.string().trim().min(1).max(2000).nullish(),
  })
  .strict()
  .superRefine((value, ctx) => {
    // The DB says the same thing; saying it here turns a 500 into a 422 that
    // names the field.
    if (!value.resolved && value.reply) {
      ctx.addIssue({
        code: 'custom',
        path: ['reply'],
        message: 'A reply belongs to a resolved report — reopening one clears it.',
      })
    }
  })
